import * as XLSX from 'xlsx';
import { PREVIEW_ROW_FLASH_IMPORT, flowTypeFromPreviewType } from './flow_preview_constants';

export { PREVIEW_ROW_FLASH_IMPORT, flowTypeFromPreviewType } from './flow_preview_constants';

/** Table headers in b. Preview (aligned with preliminary / backend naming). */
export const PREVIEW_FLOW_COLUMNS = [
  'date',
  'hour',
  'desc',
  'raw_type',
  'flow_type',
  'flow_value',
  'flow_currency',
  'extras_description',
  'categ_l1',
  'extras_id',
  'status',
  'external_id',
  'extras_cardnumber',
  'payee_mcc',
  'purchase_date',
  'total_installments',
  'installment_number',
];

export {
  PREVIEW_IMPORT_JSON_ACCOUNT_ID,
  PREVIEW_IMPORT_EXCEL_ACCOUNT_ID,
  PREVIEW_JSON_KEY_PREFIX,
  PREVIEW_EXCEL_KEY_PREFIX,
  makePreviewImportJsonKey,
  makePreviewImportExcelKey,
  isJsonImportKey,
  isExcelImportKey,
  removePreviewEntriesMatchingAccountName,
  normalizePreviewAccountName,
} from './preview_map_utils';

const str = (v) => (v === undefined || v === null ? '' : String(v));

/** Parse ISO 8601 (e.g. …Z) to UTC calendar date and time-of-day strings (no offset applied). */
export function isoUtcDateAndHour(isoStr) {
  const s = String(isoStr || '');
  const m = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(?:\.\d+)?Z?$/i);
  if (m) return { datePart: m[1], hourPart: m[2] };
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return { datePart: '', hourPart: '' };
  const iso = d.toISOString();
  return { datePart: iso.slice(0, 10), hourPart: iso.slice(11, 19) };
}

/** Pick value from object by keys (first match), case-insensitive key fallback */
function pick(raw, keys) {
  if (!raw || typeof raw !== 'object') return '';
  for (const k of keys) {
    if (raw[k] !== undefined && raw[k] !== null && raw[k] !== '') return raw[k];
  }
  const lowerMap = {};
  Object.keys(raw).forEach((k) => {
    lowerMap[k.toLowerCase()] = raw[k];
  });
  for (const k of keys) {
    const v = lowerMap[k.toLowerCase()];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return '';
}

/**
 * Normalize loose / Excel row into canonical preview row (preliminary-aligned field names).
 */
export function normalizePreviewRow(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const rawType = str(
    pick(raw, [
      'raw_type',
      'type',
      'Type',
      'transactionType',
      'operationType',
    ])
  );

  const row = {
    date: str(pick(raw, ['date', 'Date', 'transactionDate'])),
    hour: str(pick(raw, ['hour', 'Hour', 'time'])),
    desc: str(pick(raw, ['desc', 'description', 'Description', 'memo'])),
    raw_type: rawType,
    flow_type: flowTypeFromPreviewType(rawType),
    flow_value: pick(raw, ['flow_value', 'amount', 'Amount', 'value', 'Value']),
    flow_currency: str(
      pick(raw, ['flow_currency', 'currencyCode', 'currency', 'Currency', 'curr'])
    ),
    extras_description: str(
      pick(raw, ['extras_description', 'descriptionRaw', 'description_raw', 'rawDescription'])
    ),
    categ_l1: str(pick(raw, ['categ_l1', 'category', 'Category'])),
    extras_id: str(pick(raw, ['extras_id', 'categoryId', 'category_id', 'CategoryId'])),
    status: str(pick(raw, ['status', 'Status'])),
    external_id: str(pick(raw, ['external_id', 'id', 'Id', 'transactionId', 'externalId'])),
    extras_cardnumber: str(
      pick(raw, ['extras_cardnumber', 'cardNumber', 'card_number', 'creditCardMetadata.cardNumber'])
    ),
    payee_mcc: str(pick(raw, ['payee_mcc', 'payeeMCC', 'payee_mcc', 'mcc'])),
    purchase_date: str(pick(raw, ['purchase_date', 'purchaseDate'])),
    total_installments: pick(raw, ['total_installments', 'totalInstallments']),
    installment_number: pick(raw, ['installment_number', 'installmentNumber']),
  };

  if (raw.creditCardMetadata && typeof raw.creditCardMetadata === 'object') {
    const cc = raw.creditCardMetadata;
    if (!row.extras_cardnumber) row.extras_cardnumber = str(cc.cardNumber);
    if (!row.payee_mcc) row.payee_mcc = str(cc.payeeMCC);
    if (!row.purchase_date) row.purchase_date = str(cc.purchaseDate);
    if (row.total_installments === '' || row.total_installments === undefined)
      row.total_installments = cc.totalInstallments;
    if (row.installment_number === '' || row.installment_number === undefined)
      row.installment_number = cc.installmentNumber;
  }

  let fv = row.flow_value;
  if (fv !== '' && fv !== undefined && fv !== null) {
    const n = Number(fv);
    row.flow_value = Number.isNaN(n) ? fv : n;
  } else {
    row.flow_value = '';
  }

  if (!row.flow_currency) row.flow_currency = 'BRL';

  row[PREVIEW_ROW_FLASH_IMPORT] = false;

  const hasSignal =
    row.date ||
    row.desc ||
    row.flow_value !== '' ||
    row.external_id ||
    row.raw_type;
  if (!hasSignal) return null;

  return row;
}

/** Pluggy API transaction → canonical preview row */
export function pluggyTransactionToPreviewRow(tx) {
  if (!tx || typeof tx !== 'object') return null;
  const cc = tx.creditCardMetadata;
  const rawType = str(tx.type);
  const row = normalizePreviewRow({
    date: tx.date,
    hour: '',
    desc: tx.description,
    raw_type: rawType,
    type: rawType,
    flow_value: tx.amount,
    flow_currency: tx.currencyCode,
    extras_description: tx.descriptionRaw,
    categ_l1: tx.category,
    extras_id: tx.categoryId,
    status: tx.status,
    external_id: tx.id,
    extras_cardnumber: cc?.cardNumber,
    payee_mcc: cc?.payeeMCC,
    purchase_date: cc?.purchaseDate,
    total_installments: cc?.totalInstallments,
    installment_number: cc?.installmentNumber,
  });
  if (row) {
    row.flow_type = flowTypeFromPreviewType(rawType);
    row.raw_type = rawType;
  }
  return row;
}

/**
 * Flash statement item → canonical preview row.
 * Amount in centavos. UTC date/hour from ISO. extras_cardnumber = lastFourDigits only.
 */
export function flashStatementItemToPreviewRow(item) {
  if (!item || typeof item !== 'object') return null;
  const cents = Number(item.amount);
  const flow_value = Number.isFinite(cents) ? Math.round(cents) / 100 : '';
  const rawType = str(item.type);
  const external_id = str(item._id) || str(item.aggregateId);
  const row = {
    date: str(item.date),
    hour: '',
    desc: str(item.description),
    raw_type: rawType,
    flow_type: flowTypeFromPreviewType(rawType),
    flow_value,
    flow_currency: 'BRL',
    extras_description: '',
    categ_l1: '',
    extras_id: '',
    status: str(item.status),
    external_id,
    extras_cardnumber: str(item.lastFourDigits),
    payee_mcc: '',
    purchase_date: '',
    total_installments: '',
    installment_number: '',
    [PREVIEW_ROW_FLASH_IMPORT]: true,
  };

  const hasSignal =
    row.date || row.desc || row.flow_value !== '' || row.external_id || row.raw_type;
  if (!hasSignal) return null;
  return row;
}

function extractFlashStatementItems(parsed) {
  if (!Array.isArray(parsed) || parsed.length === 0) {
    throw new Error('Flash JSON must be a non-empty array (e.g. statement response)');
  }
  const first = parsed[0];
  const items = first?.result?.data?.json?.items;
  if (!Array.isArray(items)) {
    throw new Error('Flash JSON: missing result.data.json.items on first element');
  }
  return items;
}

/**
 * JSON text → preview rows.
 * @param {'flash'|'simple'} [opts.mode='simple']
 */
export function convertJsonTextToPreviewRows(text, opts = {}) {
  const mode = opts.mode === 'flash' ? 'flash' : 'simple';
  const trimmed = String(text || '').trim();
  if (!trimmed) throw new Error('JSON is empty');

  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new Error('Invalid JSON');
  }

  if (mode === 'flash') {
    const items = extractFlashStatementItems(parsed);
    const rows = items.map((item) => flashStatementItemToPreviewRow(item)).filter(Boolean);
    if (rows.length === 0) {
      throw new Error('No transaction rows could be parsed from Flash JSON');
    }
    return rows;
  }

  const arr = Array.isArray(parsed) ? parsed : parsed?.transactions;
  if (!Array.isArray(arr)) {
    throw new Error('Expected a JSON array or an object with a "transactions" array');
  }

  const rows = [];
  arr.forEach((item) => {
    const row = typeof item === 'object' && item !== null ? normalizePreviewRow(item) : null;
    if (row) rows.push(row);
  });

  if (rows.length === 0) {
    throw new Error('No transaction rows could be parsed from JSON');
  }
  return rows;
}

/**
 * Excel workbook → preview rows.
 */
export function parseExcelToPreviewRows(arrayBuffer, opts = {}) {
  const sheetName = opts.sheetName != null ? String(opts.sheetName).trim() : 'data';
  const headerRow = Math.max(1, parseInt(opts.headerRow, 10) || 1);

  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const ws = wb.Sheets[sheetName];
  if (!ws) {
    const names = wb.SheetNames?.join(', ') || '(none)';
    throw new Error(`Sheet "${sheetName}" not found. Available: ${names}`);
  }

  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false });
  const headerIdx = headerRow - 1;
  const headers = (aoa[headerIdx] || []).map((h) => String(h).trim());
  if (!headers.some((h) => h)) {
    throw new Error(`Header row ${headerRow} is empty`);
  }

  const rows = [];
  for (let r = headerIdx + 1; r < aoa.length; r += 1) {
    const cells = aoa[r] || [];
    const obj = {};
    headers.forEach((h, i) => {
      if (!h) return;
      obj[h] = cells[i];
    });
    const normalized = normalizePreviewRow(obj);
    if (normalized) rows.push(normalized);
  }

  if (rows.length === 0) {
    throw new Error('No data rows found below the header');
  }
  return rows;
}
